// MapPanelMapLibre.tsx - Fixed implementation

import React, { useState, useRef, useEffect, useCallback } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

import { Location } from '../types/chat';
import { MapInfoCard } from './MapInfoCard';
import { MarkerTooltip } from './MarkerTooltip';
import { mapStateService } from '../services/dataPersistence';
import { getPlacePhotos } from '../services/places';
import { panelAnimator } from '../services/panelAnimationService';
import { mapPaddingService } from '../services/mapPaddingService';
import { createPin, COLOR_SEQUENCE } from '../utils/markerUtils';
import { X, ChevronLeft, ChevronRight, MapPin, Star, Landmark, Building, Castle, 
         MenuIcon as Monument, Church, Replace as Palace, Mouse as Museum, Mountain, 
         Trees as Tree, Palmtree, Tent, Warehouse, UtensilsCrossed, Waves, Umbrella, 
         Flower2, Camera, Wine, Coffee, ShoppingBag, Ticket } from 'lucide-react';

//import { getUnsplashPhotos } from '../services/unsplash';

interface MapPanelMapLibreProps {
  locations: Location[];
  onLocationSelect: (location: Location | null) => void;
  isLoading: boolean;
  isStreaming: boolean;
  selectedLocation: Location | null;
  isProcessingLocation: boolean;
  showPopupForSelected?: boolean;
  chatPanelVisible?: boolean;
  chatPanelRef?: React.RefObject<HTMLDivElement>;
}

export const MapPanelMapLibre: React.FC<MapPanelMapLibreProps> = ({ 
  locations,  
  onLocationSelect,
  isLoading,
  isStreaming,
  selectedLocation,
  isProcessingLocation = false,
  showPopupForSelected = false,
  chatPanelVisible = true 
}) => {

  const isResetViewRef = useRef(false);
  // ===== STATE VARIABLES =====
  const prevSelectedLocationRef = useRef<Location | null>(selectedLocation);
  const [locationImages, setLocationImages] = useState<string[]>([]);
  const [showInfoCard, setShowInfoCard] = useState(false);
  const [selectedMarkerLocation, setSelectedMarkerLocation] = useState<Location | null>(null);
  const [markerPosition, setMarkerPosition] = useState<{ x: number, y: number } | null>(null);
  
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const markersRef = useRef<{[id: string]: maplibregl.Marker}>({});
  
  const [isMapAnimating, setIsMapAnimating] = useState(false);
  const isAnimatingRef = useRef(false);
  const [activeMarkerId, setActiveMarkerId] = useState<string | null>(null);
  const [hoveredMarkerId, setHoveredMarkerId] = useState<string | null>(null);
  const [hoveredLocation, setHoveredLocation] = useState<Location | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [isLoadingImages, setIsLoadingImages] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number, y: number } | null>(null);
  
  const autoFlowTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isAutoFlowActiveRef = useRef(false);
  const prevLocationsKeyRef = useRef<string>('');
  const locationDebounceRef = useRef<NodeJS.Timeout | null>(null);
  
  const [useNativeMarkers, setUseNativeMarkers] = useState(true);
  const animatedMarkersRef = useRef<Set<string>>(new Set());
  const animatingMarkersRef = useRef<Set<string>>(new Set()); // Track currently animating markers
  const previousLocationIdsRef = useRef<Set<string>>(new Set());

  const [isCardExpanded, setIsCardExpanded] = useState(false);
  const currentBoundsRef = useRef<maplibregl.LngLatBounds | null>(null);
  
  const isBoundsAnimatingRef = useRef(false);
  const pendingBoundsRef = useRef<maplibregl.LngLatBounds | null>(null);
  // Add these refs at the top:
  const dayBoundsAnimationRef = useRef<NodeJS.Timeout | null>(null);
  const animatedDaysRef = useRef(0);

  const isItineraryCompleteRef = useRef(false);
  const isFlyingToLocationRef = useRef(false);


  
  


  // Add this ref at the top with other refs
  const isUserInteractingRef = useRef(false);

  // Configurable sizes
  const baseWidth = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--marker-base-width') || '32');
  const baseHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--marker-base-height') || '40');

  // At component level, trim the values
  const activeScale = getComputedStyle(document.documentElement).getPropertyValue('--marker-active-scale').trim() || '1.3';
  const hoverScale = getComputedStyle(document.documentElement).getPropertyValue('--marker-hover-scale').trim() || '1.15'; 
  const defaultScale = getComputedStyle(document.documentElement).getPropertyValue('--marker-default-scale').trim() || '1';

  // Add these state variables at the top of MapPanelMapLibre:
  const [pendingCardLocation, setPendingCardLocation] = useState<Location | null>(null);
  const [mapIsStable, setMapIsStable] = useState(true);
  const boundsDebounceRef = useRef<NodeJS.Timeout | null>(null);
  // Add these refs at the top:
  const hasStartedFirstBoundsRef = useRef(false);
  const firstBoundsCompleteRef = useRef(false);

  


  useEffect(() => {
    console.log('[STATE DEBUG] Current states:', {
      activeMarkerId,
      showInfoCard,
      isUserInteractingRef: isUserInteractingRef.current,
      selectedLocation: selectedLocation?.name
    });
  }, [activeMarkerId, showInfoCard, selectedLocation]);

  // Add this to your component
  const MAP_STYLES_OPT1 = {
    // MapTiler Streets (Google-like quality)
    maptiler: {
      version: 8,
      sources: {
        'maptiler': {
          type: 'raster',
          tiles: [`https://api.maptiler.com/maps/streets-v2/{z}/{x}/{y}.png?key=${process.env.REACT_APP_MAPTILER_KEY}`],
          tileSize: 256,
          attribution: '© MapTiler'
        }
      },
      layers: [{
        id: 'maptiler-layer',
        type: 'raster',
        source: 'maptiler'
      }]
    },
    
    // Stadia Maps (Clean, modern)
    stadia: {
      version: 8,
      sources: {
        'stadia': {
          type: 'raster',
          tiles: ['https://tiles.stadiamaps.com/tiles/alidade_smooth/{z}/{x}/{y}.png'],
          tileSize: 256
        }
      },
      layers: [{
        id: 'stadia-layer',
        type: 'raster',
        source: 'stadia'
      }]
    },
    
    // ESRI World Imagery (Satellite)
    satellite: {
      version: 8,
      sources: {
        'esri': {
          type: 'raster',
          tiles: ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'],
          tileSize: 256
        }
      },
      layers: [{
        id: 'esri-layer',
        type: 'raster',
        source: 'esri'
      }]
    },
    
    // CartoDB Positron (Clean, minimal)
    cartodb: {
      version: 8,
      sources: {
        'cartodb': {
          type: 'raster',
          tiles: ['https://a.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png'],
          tileSize: 256
        }
      },
      layers: [{
        id: 'cartodb-layer',
        type: 'raster',
        source: 'cartodb'
      }]
    }
  };

  // Add state for current style
  const [currentMapStyle, setCurrentMapStyle] = useState('maptiler');

  // Function to swap map style
  const swapMapStyle = (styleName: string) => {
    if (!mapRef.current) return;
    
    mapRef.current.setStyle(MAP_STYLES_OPT1[styleName]);
    
    // Re-add markers after style loads
    mapRef.current.once('styledata', () => {
      // Markers are preserved automatically in MapLibre!
      //console.log('Map style swapped to:', styleName);
    });
  };

  

  // ===== MAP STYLE OPTIONS =====
 /*  const googleMapTypes = {
    roadmap: 'r',
    satellite: 's', 
    hybrid: 'h',
    terrain: 'p'
  }; */

  /* const styleOptions_OG = {
    version: 8,
    sources: {
      'google-tiles': {
        type: 'raster',
        tiles: [`https://mt1.google.com/vt/lyrs=${googleMapTypes.roadmap}&x={x}&y={y}&z={z}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`],
        tileSize: 256
      }
    },
    layers: [{
      id: 'google-layer',
      type: 'raster',
      source: 'google-tiles'
    }]
  }; */

/*   const styleOptions = {
    version: 8,
    sources: {
      'google-tiles': {
        type: 'raster',
        tiles: [`https://mt1.google.com/vt/lyrs=${googleMapTypes.roadmap}&x={x}&y={y}&z={z}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`],
        tileSize: 256
      }
    },
    layers: [
      {
        id: 'background',
        type: 'background', 
        paint: {
          'background-color': '#e8e8e8' // Light gray instead of white
        }
      },
      {
        id: 'google-layer',
        type: 'raster',
        source: 'google-tiles'
      }
    ]
  }; */



  const MAP_PROVIDERS_OG = {
    //google: styleOptions, // Your current
    carto: { /* CartoDB config */ },
    osm: { /* OSM config */ },
    stamen: { /* Stamen config */ }
  };

  // Quick swap function
  const testMapProvider = (provider: string) => {
    if (mapRef.current) {
      mapRef.current.setStyle(MAP_PROVIDERS[provider]);
    }
  };

  const defaultCenter: [number, number] = [20, 0];
  const defaultZoom = 2.5;
  


  // Add this where you have setMarkerPosition
  const setMarkerPositionTracked = (newPosition) => {
    console.log('📍 BEFORE setMarkerPosition:', {
      oldMarker: markerPosition,
      newMarker: newPosition,
      diff: markerPosition ? {
        x: newPosition.x - markerPosition.x,
        y: newPosition.y - markerPosition.y
      } : 'first position'
    });
    
    setMarkerPosition(newPosition);
  };


  // Track InfoCard actual DOM position
  useEffect(() => {
    if (showInfoCard && markerPosition) {
      // Small delay to catch after animation
      const checkPositions = () => {
        const card = document.querySelector('.map-info-card'); // Adjust selector
        if (card) {
          const rect = card.getBoundingClientRect();
          /* console.log('🎯 Position Check:', {
            marker: markerPosition,
            card: { x: rect.left, y: rect.top },
            offset: {
              x: rect.left - markerPosition.x,
              y: rect.top - markerPosition.y
            }
          }); */
        }
      };
      
      // Check immediately and after animation
      checkPositions();
      setTimeout(checkPositions, 300); // Adjust based on animation duration
    }
  }, [showInfoCard, markerPosition]);



  // Filter for valid locations
  const validLocations = locations.filter(loc => 
    loc.position && 
    !isNaN(loc.position.lat) && 
    !isNaN(loc.position.lng) &&
    loc.position.lat >= -90 && 
    loc.position.lat <= 90 &&
    loc.position.lng >= -180 && 
    loc.position.lng <= 180
  );


  // Add this effect to listen for budget generation:
  useEffect(() => {
    const handleBudgetStarted = (e: Event) => {
      console.log('[Map] Budget generation started - itinerary complete ----- ');
      isItineraryCompleteRef.current = true;
      
      // Trigger auto-selection after a short delay
      setTimeout(() => {
        if (validLocations.length > 0 && !activeMarkerId && !showInfoCard) {
          const firstLocation = validLocations[0];
          console.log('[Map] Auto-selecting first location after budget signal');
          
          isUserInteractingRef.current = true;
          setActiveMarkerId(firstLocation.id);
          setSelectedMarkerLocation(firstLocation);
          onLocationSelect(firstLocation);
        }
      }, 1000); // Wait 1 second after budget starts
    };
    
    window.addEventListener('itinerary-budget-started', handleBudgetStarted);
    return () => window.removeEventListener('itinerary-budget-started', handleBudgetStarted);
  }, [validLocations, activeMarkerId, showInfoCard, onLocationSelect]);


  // ===== HELPER FUNCTIONS =====
  
  // Handler for image updates
/*   const handleImagesUpdate = (newImages: string[]) => {
    setLocationImages(newImages);
  };

  const handleExpandedChange_UNUSED = useCallback((isExpanded: boolean) => {
    if (isExpanded && selectedMarkerLocation) {
      // Load more images when expanded
      fetchPlaceImages(selectedMarkerLocation, 20);
    }
  }, [selectedMarkerLocation]); */

  // Add this helper function near the top of your component:
  const areBoundsEqual = (bounds1: maplibregl.LngLatBounds | null, bounds2: maplibregl.LngLatBounds) => {
    if (!bounds1) return false;
    
    return bounds1.getNorth() === bounds2.getNorth() &&
          bounds1.getSouth() === bounds2.getSouth() &&
          bounds1.getEast() === bounds2.getEast() &&
          bounds1.getWest() === bounds2.getWest();
  };

  const handleExpandedChange = useCallback((isExpanded: boolean) => {
    setIsCardExpanded(isExpanded); // ADD THIS
    // Do nothing - use same images for both views
    //console.log('[MapLibre] Card expanded:', isExpanded);
  }, []);

  const fetchPlaceImages = async (location: Location, limit: number = 5) => {
    console.log('[MapLibre] Fetching Unsplash images for:', location.name);
    if (!location) return;
    
    setLocationImages([]);
    setIsLoadingImages(true);
    
    try {
      // Use Unsplash instead of Google Places
      //const result = await getUnsplashPhotos(location.name, limit);
      //console.log('[MapLibre] Fetching images for location:', location, 'limit:', limit);
      const result = await getPlacePhotos(location, { maxPhotos: limit });

      
      const validPhotos = result.photos.filter(url => 
        typeof url === 'string' && url.trim() !== ''
      );

      console.log('[MapLibre] Valid photos count:', validPhotos); // Add this
      
      if (validPhotos.length > 0) {
        setLocationImages(validPhotos);
      } else {
        setLocationImages(['/placeholder-image.png']);
      }
      
      if (result.description && (!location.description || location.description === 'No description')) {
        const updatedLocation = {
          ...location,
          description: result.description
        };
        //setSelectedMarkerLocation(updatedLocation);
      }
    } catch (error) {
      console.error('[MapLibre] Error fetching images:', error);
      setLocationImages(['/placeholder-image.png']);
    } finally {
      setIsLoadingImages(false);
    }
  };

  // Fetch place images
/*   const fetchPlaceImages_UNUSED_DO_NOT_USE_PAID_API = async (location: Location, limit: number = 10) => {
    //console.log('[MapLibre] Fetching images for location:', location, 'limit:', limit);
    if (!location) return;
    
    setLocationImages([]);
    setIsLoadingImages(true);
    
    try {
      console.log(' [MapLibre] CALLED HERE -------------------------------',);
      const result = await getPlacePhotos(location, { maxPhotos: limit });
      const validPhotos = result.photos.filter(url => 
        typeof url === 'string' && url.trim() !== ''
      );

      //console.log('[MapLibre] Valid photos count:', validPhotos.length); // Add this

      
      if (validPhotos.length > 0) {
        setLocationImages(validPhotos);
      } else {
        setLocationImages(['/placeholder-image.png']);
      }
      
      if (result.description && (!location.description || location.description === 'No description')) {
        const updatedLocation = {
          ...location,
          description: result.description
        };
        setSelectedMarkerLocation(updatedLocation);
      }
    } catch (error) {
      console.error('[MapLibre] Error fetching place images, using fallback:', error);
      setLocationImages(['/placeholder-image.png']);
    } finally {
      setIsLoadingImages(false);
    }
  }; */

  // Handle marker click - fixed position calculation
  const handleMarkerClick = useCallback((location: Location) => {
    //console.log('[MapLibre] Marker clicked, map moving:', mapRef.current?.isMoving());

    if (!isAutoFlowActiveRef.current) {
      isFlyingToLocationRef.current = true;
    }

    // Always clear first to ensure re-selection works
    setShowInfoCard(false);
    console.log('[MapLibre] ShowInfoCard set to False: 1 ');
    setMarkerPosition(null);
    setLocationImages([]);
    setSelectedMarkerLocation(null);

    // Clear parent selection to ensure effect triggers
    onLocationSelect(null);

    if (isAutoFlowActiveRef.current) {
      isAutoFlowActiveRef.current = false;
      if (autoFlowTimeoutRef.current) {
        clearTimeout(autoFlowTimeoutRef.current);
        autoFlowTimeoutRef.current = null;
      }
      
      if (mapRef.current) {
        mapRef.current.stop();
      }
    }

    isUserInteractingRef.current = true; // ADD THIS


    setIsClosing(false);
    
    // Dispatch event to open accordion and highlight activity
    window.dispatchEvent(new CustomEvent('tripper-marker-clicked', {
      detail: { locationId: location.id }
    }));

    setTimeout(() => {
      setSelectedMarkerLocation(location);
      onLocationSelect(location);
      setActiveMarkerId(location.id);

      console.log('[MapLibre] Setting Active Marker ID --- 1 :', location.name);

      fetchPlaceImages(location); // ADD THIS - fetch only on click
    }, 50);  
    /* setTimeout(() => {
      setSelectedMarkerLocation(location);
      onLocationSelect(location); // This will now trigger effect even if same location
      setActiveMarkerId(location.id);
    }, 50); */

  }, [onLocationSelect]);

  // Create native MapLibre marker - fixed text color for visibility

  const createNativeMapLibreMarker = useCallback((
    location: Location, 
    index: number, 
    isActive: boolean, 
    isHovered: boolean,
    isNewMarker: boolean = false
  ) => {
    const hasDays = validLocations.some(loc => 'dayNumber' in loc);
    
    // Use the MARKER_COLORS from your utils
    const MARKER_COLORS_OG = {
      blue: '#4285F4',
      green: '#19A673',
      purple: '#B56AFF',
      red: '#F74A56',
      yellow: '#FBC02D',
      orange: '#FF8127',
      pink: '#F848C7',
      bloodRed: '#EC191D'
    };

    const MARKER_COLORS = {
      blue: '#A8C5FF',    // Light sky blue
      green: '#8FD4BA',   // Soft mint green
      purple: '#DDB5FF',  // Lavender
      red: '#FFB3B8',     // Soft coral
      yellow: '#FFE4A1',  // Pale yellow
      orange: '#FFD0B5',  // Peach
      pink: '#FFC4E8',    // Baby pink
      bloodRed: '#FF9FA1' // Dusty rose
    };
    
    let markerColor = MARKER_COLORS.blue; // Default blue
    if (hasDays) {
      const dayNumber = (location as any).dayNumber;
      if (dayNumber && dayNumber > 0) {
        const colorKey = COLOR_SEQUENCE[(dayNumber - 1) % COLOR_SEQUENCE.length];
        markerColor = MARKER_COLORS[colorKey];
      }
    }
    
    //const scale = isActive ? activeScale : isHovered ? hoverScale : defaultScale;
    //style="text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);"

   /* <!-- Ground Shadow -->
    <ellipse 
    cx="23" 
    cy="56" 
    rx="12" 
    ry="4" 
    fill="rgba(0,0,0,0.2)" 
    filter="url(#groundShadow-${location.id})" 
    /> */

    //const width = baseWidth * scale;
    //const height = baseHeight * scale;
    
    const el = document.createElement('div');
    el.className = 'native-maplibre-marker';
    el.setAttribute('data-marker-id', location.id);
    
    const showNumber = locations.length > 1;
    const displayNumber = (index + 1) < 10 ? (index + 1) : (index + 1).toString().padStart(2, '0');
    
    el.innerHTML = `
        <div class="pin-wrapper" style="transform-origin: bottom center; transition: transform 0.2s ease-out;">
        <svg width="${baseWidth}" height="${baseHeight + 8}" viewBox="0 0 46 60" style="overflow: visible;">
          <!-- Filters -->
          <defs>
            <filter id="dropShadow-${location.id}" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur in="SourceAlpha" stdDeviation="2"/>
              <feOffset dx="1" dy="1" result="offsetblur"/>
              <feComponentTransfer>
                <feFuncA type="linear" slope="0.5"/>
              </feComponentTransfer>
              <feMerge>
                <feMergeNode/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
            
            <filter id="groundShadow-${location.id}" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur in="SourceAlpha" stdDeviation="2"/>
              <feComponentTransfer>
                <feFuncA type="linear" slope="0.3"/>
              </feComponentTransfer>
            </filter>
            
            <linearGradient id="shineGradient-${location.id}" x1="50%" y1="0%" x2="50%" y2="100%">
              <stop offset="0%" stop-color="#FFFFFF" stop-opacity="0.4"/>
              <stop offset="100%" stop-color="#FFFFFF" stop-opacity="0"/>
            </linearGradient>
          </defs>
          
          
          <!-- Pin Body with Stroke and Shadow -->
          
          <circle 
            cx="23" 
            cy="23" 
            r="16" 
            fill="${markerColor}"
            stroke="#FFFFFF"
            stroke-width="2.5"
            filter="url(#dropShadow-${location.id})"
          />
    
          ${!showNumber ? `
            <!-- Shine overlay for unnumbered pins -->
            <path 
              d="M23 3C14.2 3 7 10.2 7 19C7 31 23 47 23 47C23 47 39 31 39 19C39 10.2 31.8 3 23 3Z" 
              fill="url(#shineGradient-${location.id})" 
              opacity="0.5"
            />
          ` : `
            <!-- Text for numbered pins -->
            <text 
              x="23" 
              y="24" 
              font-family="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" 
              font-size="18" 
              font-weight="500" 
              fill="#151e26" 
              text-anchor="middle" 
              dominant-baseline="central"
            >
              ${displayNumber}
            </text>
          `}
        </svg>
      </div>
    `;
    
    if (isActive) el.classList.add('marker-active');
    if (isHovered) el.classList.add('marker-hover');
    
    return el;
  }, [locations.length, validLocations]);

  // Handle close info card
  const handleCloseInfoCard = useCallback(() => {
    isUserInteractingRef.current = false;
    console.log('[MapLibre] SETTING INFO CARD AND USER INTERACTION REF TO FALSE -- FOKIN HELLLLLL ---------------------- 1111 ');

    setIsClosing(true);
    
    setTimeout(() => {
      setShowInfoCard(false);
      console.log('[MapLibre] ShowInfoCard set to False: 2 ');
      setSelectedMarkerLocation(null);
      setMarkerPosition(null);
      setLocationImages([]);
      onLocationSelect(null); // IMPORTANT: Clear parent's selectedLocation
      setIsClosing(false);
      setActiveMarkerId(null);
      console.log('[MapLibre] Setting Active Marker ID to NULL');

    }, 150);
  }, [onLocationSelect]);

  // Handle map click
/*   const handleMapClick_UNUSED = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    
    if (
      target.closest('.map-info-card') || 
      target.closest('.maplibregl-marker') ||
      target.closest('button') ||
      target.tagName.toLowerCase() === 'button' ||
      target.tagName.toLowerCase() === 'img'
    ) {
      return;
    }
    
    setHoveredMarkerId(null);
    setHoveredLocation(null);
  }, [handleCloseInfoCard]); */


  // Handle map click - modified to match close button animation
  const handleMapClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    
    if (
      target.closest('.map-info-card') || 
      target.closest('.maplibregl-marker') ||
      target.closest('button') ||
      target.tagName.toLowerCase() === 'button' ||
      target.tagName.toLowerCase() === 'img'
    ) {
      return;
    }
    
    // RESET USER INTERACTION on map click
    isUserInteractingRef.current = false;
    console.log('[MapLibre] SETTING INFO CARD AND USER INTERACTION REF TO FALSE -- FOKIN HELLLLLL ---------------------- 2222');

    setHoveredMarkerId(null);
    setHoveredLocation(null);

    //setShowInfoCard(false);
    //setMarkerPosition(null);
    //setLocationImages([]);
    //setSelectedMarkerLocation(null);
    isFlyingToLocationRef.current = false;
    //onLocationSelect(null);
    //setIsClosing(false);
    

    // Don't immediately close - trigger the animation first
    /* if (showInfoCard) {
      setIsClosing(true);
      
      // Wait for MapInfoCard's fade animation (300ms based on your CSS)
      setTimeout(() => {
      }, 600); // Give time for opacity transition
    } */
    if (showInfoCard) {
      if (isCardExpanded) {
        // If expanded, just collapse it (don't close)
        window.dispatchEvent(new CustomEvent('map-collapse-card'));
      } else {
        // If not expanded, close with fade animation
        setIsClosing(true);
        setTimeout(() => {
          handleCloseInfoCard();
        }, 300); // Match MapInfoCard's fade duration
      }
    }
  }, [showInfoCard, isCardExpanded, handleCloseInfoCard]);





  // Extract HTML from Leaflet icon
  function getIconHtml(icon: any): string {
    if (icon && icon.options && icon.options.html) {
      return icon.options.html;
    }
    
    if (icon && icon._html) {
      return icon._html;
    }
    
    if (typeof icon === 'string' && icon.includes('<')) {
      return icon;
    }
    
    if (icon && icon.options && icon.options.iconUrl) {
      return `<img src="${icon.options.iconUrl}" style="width:${icon.options.iconSize[0]}px;height:${icon.options.iconSize[1]}px;" />`;
    }
    
    if (icon && icon.innerHTML) {
      return icon.innerHTML;
    }
    
    console.warn("[MapLibre] Couldn't extract HTML from icon:", icon);
    return '';
  }

  // Create a custom trackpad pinch handler for smoother zooming
  const handleTrackpadPinch_OG = useCallback((e: WheelEvent) => {
    if (!mapRef.current || !e.ctrlKey) return;
    
    e.preventDefault();
    
    const map = mapRef.current;
    const currentZoom = map.getZoom();
    const zoomIncrement = -e.deltaY * 0.7;

    const easing = (t) => t * (2 - t);
    // Log sample values
    for (let i = 0; i <= 1; i += 0.1) {
      //console.log(`t: ${i.toFixed(1)}, eased: ${easing(i).toFixed(3)}`);
    }
    map.easeTo({
      zoom: currentZoom + zoomIncrement,
      duration: 1000,
      padding: mapPaddingService.getPadding(),
      easing: (t) => t * (2 - t)
    });
  }, []);

  /* const handleTrackpadPinch = useCallback((e: WheelEvent) => {
    if (!mapRef.current || !e.ctrlKey) return;
    
    e.preventDefault();
    
    const map = mapRef.current;
    const currentZoom = map.getZoom();
    const zoomIncrement = -e.deltaY * 0.01;
    
    // Get mouse position relative to map container
    const rect = map.getContainer().getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    map.easeTo({
      zoom: currentZoom + zoomIncrement,
      around: [x, y],  // Use 'around' not 'center'
      duration: 300,
      padding: mapPaddingService.getPadding(),
      easing: (t) => t * (2 - t)
    });
  }, []); */

  const handleTrackpadPinch = useCallback((e: WheelEvent) => {
    if (!mapRef.current || !e.ctrlKey) return;
    
    e.preventDefault();
    
    const map = mapRef.current;
    const currentZoom = map.getZoom();
    const zoomIncrement = -e.deltaY * 0.7;
    
    // Get mouse position relative to map container
    const rect = map.getContainer().getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    // Convert pixel to lng/lat BEFORE calling easeTo
    const lngLat = map.unproject([x, y]);
    
    map.easeTo({
      zoom: currentZoom + zoomIncrement,
      around: lngLat,  // Now it's valid lng/lat, not pixels
      duration: 300,
      padding: mapPaddingService.getPadding(),
      easing: (t) => t * (2 - t)
    });
  }, []);
  
const googleMapTypes = {
  roadmap: 'r',
  satellite: 's', 
  hybrid: 'h',
  terrain: 'p'
};

const REACT_APP_CARTO_KEY='eyJhbGciOiJIUzI1NiJ9.eyJhIjoiYWNfZ2QzNHY5djAiLCJqdGkiOiIxZGJmYzEyNSJ9.jJI6GA3JPjaN4_ele_671IcpW-G3f9GhGN1y9-ikseU';

const REACT_APP_MAPTILER_KEY='mNqRLcRwdoCeuCZ6QhE7'

// Combined MAP_PROVIDERS object
const MAP_PROVIDERS = {
  cartoPositron: 'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json',
  cartoDark: 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json',
  cartoVoyager: 'https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json',
  

  // MapTiler with 3D buildings built-in
  maptiler3D: `https://api.maptiler.com/maps/3d/style.json?key=${REACT_APP_MAPTILER_KEY}`,
  maptilerStreets: `https://api.maptiler.com/maps/streets-v2/style.json?key=${REACT_APP_MAPTILER_KEY}`,

  google: {
    version: 8 as const,
    sources: {
      'google-tiles': {
        type: 'raster' as const,
        tiles: [
          `https://mt0.google.com/vt/lyrs=${googleMapTypes.roadmap}&x={x}&y={y}&z={z}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`,
          `https://mt1.google.com/vt/lyrs=${googleMapTypes.roadmap}&x={x}&y={y}&z={z}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`,
          `https://mt2.google.com/vt/lyrs=${googleMapTypes.roadmap}&x={x}&y={y}&z={z}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`,
          `https://mt3.google.com/vt/lyrs=${googleMapTypes.roadmap}&x={x}&y={y}&z={z}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`
        ],
        tileSize: 256,
        maxzoom: 19,
        minzoom: 0
      }
    },
    layers: [
      {
        id: 'background',
        type: 'background' as const,
        paint: {
          'background-color': '#e8e8e8'
        }
      },
      {
        id: 'google-layer',
        type: 'raster' as const,
        source: 'google-tiles',
        paint: {
          'raster-fade-duration': 300
        }
      }
    ]
  },
  
  carto: {
    version: 8 as const,
    sources: {
      'carto-tiles': {
        type: 'raster' as const,
        tiles: ['https://basemaps.cartocdn.com/rastertiles/voyager_labels_under/{z}/{x}/{y}.png'],
        tileSize: 256,
        attribution: '© CartoDB'
      }
    },
    layers: [
      {
        id: 'background',
        type: 'background' as const,
        paint: {
          'background-color': '#f8f8f8'
        }
      },
      {
        id: 'carto-layer',
        type: 'raster' as const,
        source: 'carto-tiles'
      }
    ]
  },
  
  osm: {
    version: 8 as const,
    sources: {
      'osm-tiles': {
        type: 'raster' as const,
        tiles: [
          'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
          'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
          'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png'
        ],
        tileSize: 256,
        attribution: '© OpenStreetMap contributors'
      }
    },
    layers: [
      {
        id: 'background',
        type: 'background' as const,
        paint: {
          'background-color': '#e8e8e8'
        }
      },
      {
        id: 'osm-layer',
        type: 'raster' as const,
        source: 'osm-tiles'
      }
    ]
  },
  
  stamen: {
    version: 8 as const,
    sources: {
      'stamen-tiles': {
        type: 'raster' as const,
        tiles: ['https://stamen-tiles.a.ssl.fastly.net/toner-lite/{z}/{x}/{y}.png'],
        tileSize: 256,
        attribution: '© Stamen Design'
      }
    },
    layers: [
      {
        id: 'background',
        type: 'background' as const,
        paint: {
          'background-color': '#f0f0f0'
        }
      },
      {
        id: 'stamen-layer',
        type: 'raster' as const,
        source: 'stamen-tiles'
      }
    ]
  }
};


// Default style to use
const styleOptions = MAP_PROVIDERS.stamen; // or MAP_PROVIDERS.carto for better reliability

// First, get a free API key from https://cloud.maptiler.com/

const MAP_PROVIDERS_NO_TILES = {
  // MapTiler Streets (Google-like)
  maptilerStreets: `https://api.maptiler.com/maps/streets-v2/style.json?key=mNqRLcRwdoCeuCZ6QhE7`,
  
  // MapTiler Basic
  maptilerBasic: `https://api.maptiler.com/maps/basic-v2/style.json?key=mNqRLcRwdoCeuCZ6QhE7`,
  
  // MapTiler Bright
  maptilerBright: `https://api.maptiler.com/maps/bright-v2/style.json?key=mNqRLcRwdoCeuCZ6QhE7`,
  
  // MapTiler Satellite
  maptilerSatellite: `https://api.maptiler.com/maps/satellite/style.json?key=mNqRLcRwdoCeuCZ6QhE7`,
  
  // MapTiler Topo
  maptilerTopo: `https://api.maptiler.com/maps/topo-v2/style.json?key=mNqRLcRwdoCeuCZ6QhE7`,
  
  // MapTiler Dark
  maptilerDark: `https://api.maptiler.com/maps/darkmatter-v2/style.json?key=mNqRLcRwdoCeuCZ6QhE7`,

};



// With API key you get more styles and customization
const CARTO_AUTHENTICATED = {
  positron: `https://maps-api-v2.us.carto.com/user/public/carto/api/v1/map/named/positron/all.json?api_key=${REACT_APP_CARTO_KEY}`,
  darkMatter: `https://maps-api-v2.us.carto.com/user/public/carto/api/v1/map/named/dark-matter/all.json?api_key=${REACT_APP_CARTO_KEY}`,
  voyager: `https://maps-api-v2.us.carto.com/user/public/carto/api/v1/map/named/voyager/all.json?api_key=${REACT_APP_CARTO_KEY}`
};

const styleOptions_carto = 'https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json';

//console.log('process.env:', process.env.REACT_APP_CARTO_KEY);
//console.log('import.meta:', import.meta?.env?.REACT_APP_CARTO_KEY);


//console.log('[MapPanel] Using MapLibre with styles:', import.meta.env.REACT_APP_MAPTILER_KEY);
  // ===== EFFECTS =====

  // Initialize map
  useEffect(() => {
    if (mapContainerRef.current && !mapRef.current) {
      const map = new maplibregl.Map({
        container: mapContainerRef.current,
        style:MAP_PROVIDERS.carto,
        //style: MAP_PROVIDERS_NO_TILES.maptilerStreets,
        //style: MAP_PROVIDERS_NO_TILES.maptilerBright,
        //style: MAP_PROVIDERS.maptiler3D,//MAP_PROVIDERS_NO_TILES.maptilerStreets,
        //style: `https://api.maptiler.com/maps/streets-v2/style.json?key=${REACT_APP_MAPTILER_KEY}`,
        //style: `https://api.maptiler.com/maps/3d/style.json?key=${REACT_APP_MAPTILER_KEY}`,
        center: defaultCenter,
        zoom: defaultZoom,
        pitch: 60,
        scrollZoom: {
          speed: 0.5,
          smooth: true,
          around: 'mouse'
        },
        minZoom: parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--map-min-zoom').trim()) || 2,
        maxZoom: parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--map-max-zoom').trim()) || 14.5,
        minPitch: 0,  // UNCOMMENT THIS
        maxPitch: 55, // UNCOMMENT THIS
        /*minPitch: parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--map-pitch-min').trim()) || 0,
        maxPitch: parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--map-pitch-max').trim()) || 85, */
        renderWorldCopies: false,
         // Performance options
        maxTileCacheSize: 1000, // Cache up to 100 tiles (default is 50)
        fadeDuration: 300,
        refreshExpiredTiles: false,
        crossSourceCollisions: false,
        trackResize: true, // If container size is fixed
        // Preload adjacent tiles
        //maxPitch: 0,  // Disable pitch to reduce tile requirements
        antialias: true,  // Better performance
        attributionControl: false,
        pitchWithRotate: false,
        collectResourceTiming: false,
        preserveDrawingBuffer: false
      });

      map.addControl(new maplibregl.NavigationControl({
        showCompass: false,
        visualizePitch: false
      }), 'bottom-right');

      mapRef.current = map;
    }

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
  if (mapRef.current) {
      mapPaddingService.setMap(mapRef.current);
      
      // Apply initial padding based on current panel states
      const initialPadding = mapPaddingService.getPadding();
      mapRef.current.easeTo({
        padding: initialPadding,
        duration: 0 // Instant for initial setup
      });
    }
  }, [mapRef.current]);

  // Add this useEffect in MapPanelMapLibre.tsx after the other useEffects:
  /* useEffect(() => {
    if (selectedLocation && selectedLocation !== selectedMarkerLocation) {
      //console.log('[MapPanel] Syncing selectedMarkerLocation with selectedLocation');
      //setSelectedMarkerLocation(selectedLocation);
      //setActiveMarkerId(selectedLocation.id);
      
      // Clear any existing card to ensure clean state
      //setShowInfoCard(false);
      //setMarkerPosition(null);
      //setLocationImages([]); // Clear old images

      //console.log('[MapPanel] Clearing and fetching images for selected location:', selectedLocation); 

      //fetchPlaceImages(selectedLocation); // Add this line
      
      // The map animation useEffect will handle showing the card after flyTo completes
    }
  }, [selectedLocation]); */

  /* useEffect(() => {
    if (selectedLocation?.id) {
      console.log('[MapLibre] Syncing activeMarkerId with selectedLocation:', selectedLocation.id);
      setActiveMarkerId(selectedLocation.id);
    } else {
      setActiveMarkerId(null);
    }
  }, [selectedLocation]); */

  // Sync selectedMarkerLocation with selectedLocation prop
  // Fixed marker creation - only animate truly new markers
  useEffect(() => {
    if (!mapRef.current) return;
    
    const map = mapRef.current;
    const currentLocationIds = new Set(validLocations.map(loc => loc.id));
    
    
    // Identify truly new locations
    const newLocationIds = Array.from(currentLocationIds).filter(
      id => !previousLocationIdsRef.current.has(id)
    );
    
    // Remove markers that no longer exist
    Object.keys(markersRef.current).forEach(id => {
      if (!currentLocationIds.has(id)) {
        markersRef.current[id].remove();
        delete markersRef.current[id];
        animatedMarkersRef.current.delete(id);
      }
    });
    
    // Clear previous selected location when marker set changes
    if (newLocationIds.length > 0) {
      prevSelectedLocationRef.current = null;
      //console.log('[Markers] Cleared prevSelectedLocationRef for new marker set');
    }
    
    // Create or update markers
    validLocations.forEach((location, index) => {
      const existingMarker = markersRef.current[location.id];
      const isNewMarker = newLocationIds.includes(location.id);
      const hasDays = validLocations.some(loc => 'dayNumber' in loc);
      
      if (!existingMarker || (index === 0 && validLocations.length > 1 && !existingMarker?.getElement()?.textContent?.includes('1'))) {
        // Remove old marker if it exists
        if (existingMarker) {
          existingMarker.remove();
          delete markersRef.current[location.id];
        }
        // Create new marker
        const el = useNativeMarkers 
          ? createNativeMapLibreMarker(location, index, false, false, isNewMarker)
          : (() => {
              const element = document.createElement('div');
              element.className = 'maplibre-marker';
              
              let markerPin;
              if (hasDays) {
                const dayNumber = (location as any).dayNumber;
                const colorKey = dayNumber 
                  ? COLOR_SEQUENCE[(dayNumber - 1) % COLOR_SEQUENCE.length]
                  : 'blue';
                markerPin = createPin({ 
                  number: index + 1, 
                  color: colorKey,
                  scale: 1.2,
                  animated: isNewMarker,
                  id: location.id
                });
              } else {
                markerPin = validLocations.length > 1 
                  ? createPin({ number: index + 1, color: 'blue', scale: 1, animated: isNewMarker, id: location.id })
                  : createPin({ color: 'blue', scale: 1, animated: isNewMarker, id: location.id });
              }
              
              element.innerHTML = getIconHtml(markerPin);
              return element;
            })();
        
        // Add animation class only for truly new markers
        if (isNewMarker && !animatedMarkersRef.current.has(location.id)) {
          el.classList.add('marker-pop');
          animatedMarkersRef.current.add(location.id);
          animatingMarkersRef.current.add(location.id); // Track as animating
          
          setTimeout(() => {
            if (el && el.classList) {
              el.classList.remove('marker-pop');
            }
            animatingMarkersRef.current.delete(location.id); // Animation complete
          }, 700); // Increased from 500ms to let animation complete
        }
        
        const marker = new maplibregl.Marker({ element: el, anchor: 'bottom' })
          .setLngLat([location.position.lng, location.position.lat])
          .addTo(map);
        
        // Events
        el.addEventListener('click', () => {
          setActiveMarkerId(location.id);
          console.log('[MapLibre] Setting Active Marker ID --- 2 :', location.name);

          handleMarkerClick(location);
        });
        
        el.addEventListener('mouseenter', () => {
          console.log('[MapLibre] active marker id:', activeMarkerId);
          if (activeMarkerId !== location.id) {
            setHoveredMarkerId(location.id);
            setHoveredLocation(location);
          }
        });
        
        el.addEventListener('mouseleave', () => {
          setHoveredMarkerId(null);
          setHoveredLocation(null);
        });
        
        markersRef.current[location.id] = marker;
      }
    });
    
    // Update previous location IDs
    previousLocationIdsRef.current = currentLocationIds;
    
  }, [validLocations, useNativeMarkers, createNativeMapLibreMarker, handleMarkerClick, locations.length]); // Added locations.length

  // Update marker appearance based on hover/active state
  // Replace your marker scaling useEffect with this:
  // Replace your scale application useEffect with this:
  useEffect(() => {
    const computed = getComputedStyle(document.documentElement);
    const activeScale = computed.getPropertyValue('--marker-active-scale').trim() || '1.3';
    const hoverScale = computed.getPropertyValue('--marker-hover-scale').trim() || '1.15';
    const defaultScale = computed.getPropertyValue('--marker-default-scale').trim() || '1';
    
    Object.entries(markersRef.current).forEach(([id, marker]) => {
      if (animatingMarkersRef.current.has(id)) return;
      
      const el = marker.getElement();
      const isActive = activeMarkerId === id;
      const isHovered = hoveredMarkerId === id && !isActive;
      
      el.classList.toggle('marker-active', isActive);
      el.classList.toggle('marker-hover', isHovered);
      
      // Use CSS transform instead of SVG attribute
      const svg = el.querySelector('svg') as SVGElement;
      if (svg) {
        const scale = isActive ? activeScale : isHovered ? hoverScale : defaultScale;
        svg.style.transform = `scale(${scale})`;
        svg.style.transformOrigin = '23 56'; // SVG center bottom
        svg.style.transition = 'transform 0.2s ease-out';
      }
      
      const markerContainer = el.parentElement;
      if (markerContainer) {
        markerContainer.style.zIndex = isActive ? '3000' : isHovered ? '2000' : '1000';
      }
    });
  }, [activeMarkerId, hoveredMarkerId]);

  // Update positions of info card and tooltip
   useEffect(() => {
    if (!mapRef.current) return; 
    const updateNonMarkerPositions = () => {
      if (selectedMarkerLocation && showInfoCard && selectedMarkerLocation.position) {
        const point = mapRef.current!.project([
          selectedMarkerLocation.position.lng, 
          selectedMarkerLocation.position.lat
        ]);
        
        // Get map container's position in viewport
        const mapRect = mapContainerRef.current!.getBoundingClientRect();
        
        // Convert to viewport coordinates
        setMarkerPosition({ 
          x: point.x + mapRect.left, 
          y: point.y + mapRect.top - 30 
        });
      }

      
      // TOOLTIP - NO OFFSET NEEDED (it's inside map container)
      if (hoveredLocation && hoveredLocation.position) {
        const point = mapRef.current!.project([
          hoveredLocation.position.lng, 
          hoveredLocation.position.lat
        ]);
        
        setTooltipPosition({ 
          x: point.x,  // NO offset for tooltip
          y: point.y 
        });
      } 
    };

    const map = mapRef.current;
    map.on('move', updateNonMarkerPositions);
    map.on('zoom', updateNonMarkerPositions);
    updateNonMarkerPositions(); // Initial update
    
    return () => {
      map.off('move', updateNonMarkerPositions);
      map.off('zoom', updateNonMarkerPositions);
    };
  }, [selectedMarkerLocation, showInfoCard, hoveredLocation]); 

  // In MapPanelMapLibre.tsx

  // Handle map animation for selected location

  // Add this effect to track when activeMarkerId changes
  useEffect(() => {
    console.log('[MAP ANIMATION ACTIVE MARKER] activeMarkerId changed to:', activeMarkerId);
    
    if (activeMarkerId && markersRef.current[activeMarkerId]) {
      const marker = markersRef.current[activeMarkerId];
      const lngLat = marker.getLngLat();
      const rect = marker.getElement().getBoundingClientRect();
      
      console.log('[MAP ANIMATION ACTIVE MARKER] Details:', {
        id: activeMarkerId,
        lngLat: { lng: lngLat.lng, lat: lngLat.lat },
        screenPos: { x: rect.left, y: rect.top },
        mapPosition: mapRef.current?.project([lngLat.lng, lngLat.lat])
      });
    }
  }, [activeMarkerId]);

  useEffect(() => {
    //if (!mapRef.current) return;
    console.log('[MAP ANIMATION] Effect triggered, selectedLocation:', selectedLocation?.name);

    if (!mapRef.current || !selectedLocation) {
        console.log('[MAP ANIMATION] Exiting - no map or location');
      return;
    }

    // ALWAYS hide card immediately when animation starts
    console.log('[MAP ANIMATION] Hiding card at animation start');
    
    /* setShowInfoCard(false);
    setMarkerPosition(null);
    setLocationImages([]);
    setIsClosing(false); // Reset closing state
    isUserInteractingRef.current = false */
    const map = mapRef.current;

    console.log('[MAP ANIMATION] isAnimatingRef.current:', isAnimatingRef.current);


    // Hide card immediately when location changes
    setShowInfoCard(false);
    setMarkerPosition(null);
    setLocationImages([]);
    
    //if (isAnimatingRef.current) return;
    // Cancel any ongoing animation
    
    if (isAnimatingRef.current) {

      //const map = mapRef.current;
      map.stop(); // Stop current animation
      isAnimatingRef.current = false; // Reset the flag
      //setShowInfoCard(false);
      //setMarkerPosition(null);
      //setLocationImages([]);

      console.log('[MAP ANIMATION] Animation stopped, JESUS FUCKING CHRIST SHOWING INFO CARD NOW ----------------------');
      setShowInfoCard(false);
      setMarkerPosition(null);
      setLocationImages([]);
      setIsClosing(false); // Reset closing state
      // Add this ref at the top with other refs
      //const map = mapRef.current;
    }

      // Clear any existing info card before new animation
    if (selectedMarkerLocation?.id !== selectedLocation.id) {
      setShowInfoCard(false);
      setMarkerPosition(null);
      setLocationImages([]);
    }

    // Cancel any pending auto-flow
    if (autoFlowTimeoutRef.current) {
      clearTimeout(autoFlowTimeoutRef.current);
      autoFlowTimeoutRef.current = null;
    }
    isAutoFlowActiveRef.current = false;
    
    // In the map animation useEffect:
    const calculateDistance = (from: maplibregl.LngLat, to: { lng: number, lat: number }) => {
      const R = 6371; // Earth radius in km
      const dLat = (to.lat - from.lat) * Math.PI / 180;
      const dLng = (to.lng - from.lng) * Math.PI / 180;
      const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
        Math.cos(from.lat * Math.PI / 180) * Math.cos(to.lat * Math.PI / 180) *
        Math.sin(dLng/2) * Math.sin(dLng/2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
      return R * c; // Distance in km
    };

    // Calculate dynamic duration
    const currentCenter = map.getCenter();
    const distance = calculateDistance(currentCenter, selectedLocation.position);


    const isItineraryNavigation = 'dayNumber' in selectedLocation;

    const isAlreadyAtLocation = distance < 0.001; // Very close

    // Scale duration: 500ms minimum for same spot, up to 3500ms for far distances
    //const duration = Math.min(3500, Math.max(500, distance * 50));
    let duration: number;
    if (isAlreadyAtLocation) {
      console.log("[Map Animation] 1 Already at location, using quick animation");
      // Already here - super quick animation
      duration = 1000; // Almost instant
    } else if (isItineraryNavigation) {
      console.log("[Map Animation] 2 Itinerary navigation detected, using fixed duration");
      // Fixed duration for itinerary pins
      duration = 2000;
    } else if (distance < 0.1) {
      console.log("[Map Animation] 3 ery close location, using minimum duration");
      // Very close: minimum 1000ms
      duration = 2000;
    } else if (distance < 1) {
      console.log("[Map Animation] 4 Close location, using moderate duration");
      // Near: scale between 1000-2000ms
      duration = 2000;//2000 + (distance * 1000);
    } else {
      console.log("[Map Animation] 5 Far location, using longer duration");
      // Far: scale up to 3500ms
      duration = Math.min(3000, 1200 + (distance * 100));
    }

    //console.log('[Map Animation] Distance:', distance, 'km, Duration:', duration, 'ms, Itinerary:', isItineraryNavigation);
    
    isAnimatingRef.current = true;

    isUserInteractingRef.current = true
    isItineraryCompleteRef.current = true;
    isFlyingToLocationRef.current = true;

    setIsMapAnimating(true);
    prevSelectedLocationRef.current = selectedLocation;
    
    const onMoveEnd = () => {
      console.log('[MAP ANIMATION] MOVEEND FIRED!');
      map.off('moveend', onMoveEnd);
      isAnimatingRef.current = false;
      setIsMapAnimating(false);
      setLocationImages([]);
      

      // && isUserInteractingRef.current
      if (selectedLocation && selectedLocation.position && isUserInteractingRef.current) {
        console.log(" i am unable to get in Here MOTHERFUCKER -----------------------")
        // ADD THIS LINE
        setActiveMarkerId(selectedLocation.id);
        setSelectedMarkerLocation(selectedLocation);

        const point = map.project([
          selectedLocation.position.lng,
          selectedLocation.position.lat
        ]);

        const mapRect = mapContainerRef.current!.getBoundingClientRect();
        
        const newPosition = { 
          x: point.x + mapRect.left,
          y: point.y + mapRect.top - 30
        };
        
        //console.log('[MAP ANIMATION] Setting position:', newPosition);
        //console.log('[MAP ANIMATION] MapRect left:', mapRect.left);
        
        setMarkerPosition(newPosition);
        isUserInteractingRef.current = true;
        
        console.log('[MAP ANIMATION] Calling setShowInfoCard(true) ----------------------------');
        setShowInfoCard(true);
        
        console.log('[MAP ANIMATION] Fetching images -------------------------------',locationImages.length);
        //if (!locationImages.length) {
          console.log('[MAP ANIMATION] Fetching images -------------------------------');
          fetchPlaceImages(selectedLocation);
       // }
      } else {
        console.log('[MAP ANIMATION] No selectedLocation in onMoveEnd!');
      }
    };

    //console.log('[MAP ANIMATION] Attaching moveend listener');
    map.on('moveend', onMoveEnd);

    console.log('[MAP ANIMATION] Starting flyTo animation');
    
    const isLocationSwitch = prevSelectedLocationRef.current && prevSelectedLocationRef.current.id !== selectedLocation.id;
    
    // Then modify your existing code:
    if (isLocationSwitch) {
      map.easeTo({
        pitch: parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--map-pitch-min')),
        duration: 1750,
        padding: mapPaddingService.getPadding() // ADD THIS
      });
      
      setTimeout(() => {
        //const lngOffset = calculatePanelOffset();
        console.log('[Map Animation Flyto] ITS FUCKING HERE -------------- 1');
        if(selectedLocation.position){
          map.flyTo({
            center: [selectedLocation.position.lng, selectedLocation.position.lat],
            zoom: parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--map-default-zoom')),
            //pitch: parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--map-pitch-max')),
            duration: duration,
            essential: true,
            padding: mapPaddingService.getPadding() // ADD THIS

          });
        }
      }, 1750);
    } else {
      console.log('[Map Animation Flyto] ITS BLOODY FUCKING HERE -------------- 2');
      //console.log('[Map Animation] Executing flyTo with tilt');
      //const lngOffset = calculatePanelOffset();
      if(selectedLocation.position) {
        map.flyTo({
          center: [selectedLocation.position.lng, selectedLocation.position.lat],
          zoom: parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--map-default-zoom')),
          //pitch: parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--map-pitch-max')),
          duration: duration,
          essential: true,
          padding: mapPaddingService.getPadding() // ADD THIS

        });
      }
    }
    //}
  }, [selectedLocation]); // Fixed: removed selectedMarkerLocation to prevent conflicts

 
  // Add ref to track last panel width
  const lastPanelWidthRef = useRef<number>(0);

  // Clear state IMMEDIATELY when locations change
  useEffect(() => {
    if (validLocations.length === 0) return;
    
    const locationsKey = validLocations.map(loc => loc.id).join(',');
    
    // If this is a different location set, clear everything NOW

    console.log('[MapLibre] IS THE MOTHERFUCKNG USER INTERACTING -----@@@@@@@@@@@@@@@  ', isUserInteractingRef.current);

    if (!isItineraryCompleteRef.current && locationsKey !== prevLocationsKeyRef.current && prevLocationsKeyRef.current !== '' && !isUserInteractingRef.current) {
      console.log('[IMMEDIATE] New locations detected, clearing state NOW');
      setActiveMarkerId(null);

      setShowInfoCard(false);
      console.log('[MapLibre] SETTING INFO CARD AND USER INTERACTION REF TO FALSE -- FOKIN HELLLLLL ---------------------- 3333');
      isUserInteractingRef.current = false;
      
      setSelectedMarkerLocation(null);
      setMarkerPosition(null);
      setHoveredMarkerId(null);
      setLocationImages([]);
      onLocationSelect(null);
      
      // Stop any ongoing animations
      if (mapRef.current) {
        mapRef.current.stop();
      }
    }
  }, [validLocations.map(loc => loc.id).join(',')]); // Run immediately on location change

  useEffect(() => {
    if (!mapRef.current || validLocations.length === 0) return;

    // Only clear debounce timer, not the active auto-flow
    if (locationDebounceRef.current) {
      clearTimeout(locationDebounceRef.current);
    }
    
    //console.log('[Auto-flow] Locations changed, count:', validLocations.length, '- debouncing...');
    
    // Debounce for 500ms to wait for all locations to be added
    locationDebounceRef.current = setTimeout(() => {
      const locationsKey = validLocations.map(loc => loc.id).join(',');

      // ALWAYS CLEAR - NO CONDITIONS
      /* if (locationsKey !== prevLocationsKeyRef.current) {
        console.log('[Auto-flow] Clearing all state');
        setActiveMarkerId(null);
        //setShowInfoCard(false);
        //console.log('[MapLibre] ShowInfoCard set to False:  3');
        isUserInteractingRef.current = false;
        setSelectedMarkerLocation(null);
        setMarkerPosition(null);
        setHoveredMarkerId(null);
      } */


        // Clear state for completely new location sets
      /* if (prevLocationsKeyRef.current === '' || 
          !validLocations.some(loc => prevLocationsKeyRef.current.includes(loc.id))) {
        console.log('[Auto-flow] New location set, clearing all state');
        //setActiveMarkerId(null);

        setShowInfoCard(false);
        isUserInteractingRef.current = false;
        console.log('[MapLibre] SETTING INFO CARD AND USER INTERACTION REF TO FALSE -- FOKIN HELLLLLL ---------------------- 44444');

        setSelectedMarkerLocation(null);
        setMarkerPosition(null);
        setHoveredMarkerId(null);
      } */




      // Check if we've already handled this exact set
      if (locationsKey === prevLocationsKeyRef.current) {
        //console.log('[Auto-flow] Already handled this location set, skipping');
        return;
      }
      prevLocationsKeyRef.current = locationsKey;
      
      //console.log('[Auto-flow] Debounce complete, starting with', validLocations.length, 'locations');
      
      const map = mapRef.current!;
      
      // Check if user is interacting BEFORE fitting bounds
      //const userInteracting = activeMarkerId || showInfoCard;
      const userInteracting = activeMarkerId || showInfoCard || isUserInteractingRef.current;

      
      console.log('[Auto-flow] User interacting: ---- ', isUserInteractingRef.current);

      if (validLocations.length > 1) {
         // Skip auto-selection if this was a reset
        if (isResetViewRef.current) {
          isResetViewRef.current = false;
          return;
        }
        // Only fit bounds if user is NOT interacting
        //if (!userInteracting && !isUserInteractingRef.current) {
        /* if (!userInteracting && !isUserInteractingRef.current && !showInfoCard && !isFlyingToLocationRef.current) {
          let bounds = new maplibregl.LngLatBounds();
          validLocations.forEach(loc => bounds.extend([loc.position.lng, loc.position.lat]));
          
          //console.log('[Auto-flow] Fitting bounds...', mapPaddingService.getBoundsPadding());
          map.fitBounds(bounds, { padding: mapPaddingService.getBoundsPadding(), duration: 3000, pitch: parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--map-pitch-min')), essential: true });
        } else {
          console.log('[Auto-flow] Skipping bounds animation - user is interacting');
        } */
        
        // Skip auto-flow to first marker if user is interacting or still streaming
        if (userInteracting || isStreaming) {
          console.log('[Auto-flow] Skipping marker selection - user interacting or still streaming', userInteracting, isStreaming);
          return;
        }
        
        // Start auto-flow after bounds
        isAutoFlowActiveRef.current = true;
          
        // Calculate delay based on number of markers
        const boundsAnimationTime = userInteracting ? 0 : 3000; // No wait if bounds were skipped
        const markerCreationTime = validLocations.length * 50;
        const markerAnimationTime = 500;
        const pauseTime = 500;
        const totalDelay = boundsAnimationTime + markerCreationTime + markerAnimationTime + pauseTime;
        

        //const isItineraryBuilding = validLocations.some(loc => 'dayNumber' in loc) && isStreaming;
        // For itineraries, check if ALL days are complete
        const isItineraryType = validLocations.some(loc => 'dayNumber' in loc);

        /* if (isItineraryType) {
          // Extract expected day count from the itinerary
          const dayNumbers = validLocations
            .filter(loc => 'dayNumber' in loc)
            .map(loc => (loc as any).dayNumber);
          
          const maxDayNumber = Math.max(...dayNumbers);
          const uniqueDays = new Set(dayNumbers);
          
          // Check if we have all days (1 through maxDayNumber)
          const hasAllDays = uniqueDays.size === maxDayNumber && 
                            Array.from({length: maxDayNumber}, (_, i) => i + 1)
                                  .every(day => uniqueDays.has(day));
          
          console.log('[Auto-flow] : ', dayNumbers);
                        
          if (!hasAllDays) {
            console.log('[Auto-flow] Itinerary incomplete - waiting for all days', {
              currentDays: uniqueDays.size,
              maxDay: maxDayNumber
            });
            return;
          }
          
          // Also check if streaming is complete
          if (isStreaming) {
            console.log('[Auto-flow] Still streaming - waiting for completion');
            return;
          }
        } */

        // isItineraryCompleteRef.current
        // In your auto-flow effect, add this check:
        console.log('[MapLibre] isItineraryType: ---- ',isItineraryType, 'isItineraryCompleteRef.current:', isItineraryCompleteRef.current);
        if (isItineraryType) {
          console.log('[MapLibre] ---------- FUCK FUCK IM FINALLY IN ItineraryType ------ ',  isItineraryType , isItineraryCompleteRef.current);
          if (!isItineraryCompleteRef.current) {
            console.log('[Auto-flow] Waiting for budget generation signal');
            return;
          }
        }

        console.log('[Auto-flow] : ---- SKIPPED RETURN ---  ', isItineraryType);
        /* //if (isItineraryBuilding) {
        if (userInteracting || isItineraryBuilding) { 
          console.log('[Auto-flow] Skipping - itinerary still building');
          return;
        } */

        //console.log('isItineraryBuilding --- ',isItineraryBuilding);

        /* autoFlowTimeoutRef.current = setTimeout(() => {
          console.log('[Auto-flow] ITS HAPPENING HERE 1 ------------------:');
         const firstLocation = validLocations[0];
          //console.log('[Auto-flow] Selecting first location:', firstLocation.name);
          setMarkerPosition(null); // CLEAR OLD POSITION!
          setActiveMarkerId(firstLocation.id);
          setSelectedMarkerLocation(firstLocation);
          onLocationSelect(firstLocation);
          // Directly trigger flyTo animation
          //console.log('[Auto-flow] Flying to first location with tilt');
        }, totalDelay); */
        
        autoFlowTimeoutRef.current = setTimeout(() => {
          const firstLocation = validLocations[0];
          isUserInteractingRef.current = true; // ADD THIS!
          setMarkerPosition(null);
          setActiveMarkerId(firstLocation.id);
          console.log('[MapLibre] Setting Active Marker ID --- 1 :', firstLocation.name);
          
          setSelectedMarkerLocation(firstLocation);
          setShowInfoCard(true);
          onLocationSelect(firstLocation);
          fetchPlaceImages(firstLocation);
          console.log('[MapLibre] Show Info Card:', showInfoCard);

        }, totalDelay);


      } else if (validLocations.length === 1) {
        // Skip if user is interacting
        if (userInteracting || isStreaming) {
          //console.log('[Auto-flow] Skipping single location - user interacting or streaming');
          return;
        }
        
        isAutoFlowActiveRef.current = true;
        const markerAnimationTime = 500;
        const pauseTime = 500;
        const singleLocationDelay = markerAnimationTime + pauseTime;
        
        //console.log('[Auto-flow] Single location, delay:', singleLocationDelay, 'ms');
        
        /* autoFlowTimeoutRef.current = setTimeout(() => {
          if (!isAutoFlowActiveRef.current) {
            //console.log('[Auto-flow] Cancelled by user interaction');
            return;
          }

          autoFlowTimeoutRef.current = setTimeout(() => {
            if (!isAutoFlowActiveRef.current) {
              //console.log('[Auto-flow] Cancelled by user interaction');
              return;
            }
            
            //console.log('[Auto-flow] Single location - showing card');
            //fetchPlaceImages(location);

            setShowInfoCard(true);
            isAutoFlowActiveRef.current = false;
          }, 2000);
        }, singleLocationDelay); */

        autoFlowTimeoutRef.current = setTimeout(() => {
          if (!isAutoFlowActiveRef.current) {
            return;
          }

          const singleLocation = validLocations[0];
          isUserInteractingRef.current = true; // ADD THIS!
          setActiveMarkerId(singleLocation.id);
          console.log('[MapLibre] Setting Active Marker ID --- 3 :', singleLocation.name);

          setSelectedMarkerLocation(singleLocation);
          
          onLocationSelect(singleLocation);
          fetchPlaceImages(singleLocation);
          setShowInfoCard(true);
          isAutoFlowActiveRef.current = false;
          console.log('[MapLibre] Show Info Card:', showInfoCard);
          /* const singleLocation = validLocations[0];
          setActiveMarkerId(singleLocation.id);
          setSelectedMarkerLocation(singleLocation);
          onLocationSelect(singleLocation);
          fetchPlaceImages(singleLocation); // ADD THIS
          setShowInfoCard(true);
          isAutoFlowActiveRef.current = false; */
        }, singleLocationDelay);
      }
    }, 800); // Increased debounce to ensure streaming is detected
    
    return () => {
      if (locationDebounceRef.current) {
        //console.log('[Auto-flow] Cleanup - clearing debounce');
        clearTimeout(locationDebounceRef.current);
      }
    };
  }, [validLocations.map(loc => loc.id).join(','), activeMarkerId, showInfoCard, isStreaming]); // Add dependencies

  // Handle trackpad pinch-to-zoom
  useEffect(() => {
    if (!mapRef.current) return;
    
    const mapContainer = mapRef.current.getContainer();
    mapContainer.addEventListener('wheel', handleTrackpadPinch, { passive: false });
    
    return () => {
      mapContainer.removeEventListener('wheel', handleTrackpadPinch);
    };
  }, [handleTrackpadPinch]);

  // Fit bounds immediately when location count changes (no debounce)
  /* useEffect(() => {
    if (!mapRef.current || validLocations.length < 2) return;
    
    const map = mapRef.current;
    let bounds = new maplibregl.LngLatBounds();
    validLocations.forEach(loc => bounds.extend([loc.position.lng, loc.position.lat]));
    
    if(!showInfoCard || !activeMarkerId) {
      //console.log('[Bounds] Fitting bounds for', validLocations.length, 'locations NOW', mapPaddingService.getBoundsPadding());
      map.fitBounds(bounds, { padding: mapPaddingService.getBoundsPadding(), duration: 3000, pitch: parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--map-pitch-min')), essential: true });
    }
  }, [validLocations.length]); // Trigger on count change only */

  // Then in your effect:
  // Replace the fitBounds effect:
  useEffect(() => {
    if (!mapRef.current || validLocations.length < 2) return;
    
    const map = mapRef.current;
    
    // If this is the first bounds animation, let it play fully
    if (!hasStartedFirstBoundsRef.current) {
      hasStartedFirstBoundsRef.current = true;
      
      let bounds = new maplibregl.LngLatBounds();
      validLocations.forEach(loc => bounds.extend([loc.position.lng, loc.position.lat]));
      
      console.log('[Bounds] Starting FIRST bounds animation');
      
      map.fitBounds(bounds, { 
        padding: mapPaddingService.getBoundsPadding(), 
        duration: 3000, 
        pitch: parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--map-pitch-min')), 
        essential: true 
      });
      
      // Mark as complete after animation
      setTimeout(() => {
        firstBoundsCompleteRef.current = true;
        console.log('[Bounds] First animation complete');
      }, 3000);
      
      return;
    }
    
    // For subsequent updates, only animate if first is complete
    if (firstBoundsCompleteRef.current && !showInfoCard && !activeMarkerId && !isFlyingToLocationRef.current) {
      let bounds = new maplibregl.LngLatBounds();
      validLocations.forEach(loc => bounds.extend([loc.position.lng, loc.position.lat]));
      
      map.fitBounds(bounds, { 
        padding: mapPaddingService.getBoundsPadding(), 
        duration: 1000, // Shorter for updates
        pitch: parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--map-pitch-min')), 
        essential: true 
      });
    }
    
  }, [validLocations.length, showInfoCard, activeMarkerId]);




  // Add global styles for markers and map elements
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @keyframes markerPop {
        0% { 
          transform: translateY(-120px) scale(0.6); 
          opacity: 0;
        }
        25% {
          opacity: 1;
        }
        100% { 
          transform: translateY(0) scale(1);
        }
      }

      @keyframes markerPop {
        0% { 
          transform: translateY(-120px); 
          opacity: 0;
          animation-timing-function: cubic-bezier(0.55, 0, 1, 0.45); /* Strong ease-in (accelerate) */
        }
        15% {
          opacity: 1;
          animation-timing-function: cubic-bezier(0.55, 0, 1, 0.45); /* Keep accelerating */
        }
        60% { 
          transform: translateY(0);
          animation-timing-function: cubic-bezier(0, 0.55, 0.45, 1); /* Strong ease-out (decelerate) */
        }
        75% { 
          transform: translateY(-8px);  /* Much lower bounce - heavy ball */
          animation-timing-function: cubic-bezier(0.55, 0, 1, 0.45); /* Ease-in for fall */
        }
        100% { 
          transform: translateY(0);
        }
      }

      .marker-pop .pin-wrapper {
        animation: markerPop 0.5s linear forwards !important; /* Faster overall */
      }
      
      .maplibregl-marker {
        z-index: 1000 !important;
        pointer-events: auto !important;
        transform-origin: bottom center !important;
        width: max-content !important;
        height: max-content !important;
        cursor: pointer;
      }
      
      .map-info-card {
        z-index: 1500 !important;
      }

      .native-maplibre-marker {
        cursor: pointer;
        z-index: 1000;
        pointer-events: auto;
      }
      
      .pin-wrapper {
        transform-origin: bottom center !important;
        transition: transform 0.2s ease-out;
      }
    `;
    
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Listen for location card hover events
  useEffect(() => {
    const handleLocationHover = (e: Event) => {
      const customEvent = e as CustomEvent;
      const locationId = customEvent.detail?.locationId;
      
      if (locationId) {
        setHoveredMarkerId(locationId);
        
        const hoveredLoc = validLocations.find(loc => loc.id === locationId);
        if (hoveredLoc) {
          setHoveredLocation(hoveredLoc);
        }
      }
    };
    
    const handleLocationHoverEnd = () => {
      setHoveredMarkerId(null);
      setHoveredLocation(null);
    };
    
    window.addEventListener('tripper-location-hover', handleLocationHover);
    window.addEventListener('tripper-location-hover-end', handleLocationHoverEnd);
    
    return () => {
      window.removeEventListener('tripper-location-hover', handleLocationHover);
      window.removeEventListener('tripper-location-hover-end', handleLocationHoverEnd);
    };
  }, [validLocations]);


  /* // Add this effect to listen for content added events
  useEffect(() => {
    const setItineraryStreamingStatus = () => {
      console.log('[Maplibre] streamimng status has changed ---- ');
      // console.log('[App] Content added event received, scrolling to show new content');
      //ensureFullContentVisible();
    };
    
    // Listen for both custom events and standard chat updates
    window.addEventListener('itinerary-streaming-status', setItineraryStreamingStatus);
    
    return () => {
      window.removeEventListener('itinerary-streaming-status', setItineraryStreamingStatus);
    };
  }, []); */
  

  // Listen for map control events
  useEffect(() => {
    const handleZoomIn = () => {
      //console.log('[Map Control] ',mapRef.current);
      if (mapRef.current) {
        //console.log('[Map Control] Zooming in');
        const currentZoom = mapRef.current.getZoom();
        mapRef.current.easeTo({
          zoom: currentZoom + 1,
          duration: 3500,
          easing: (t) => t * (2 - t)
        });
      }
    };
    
    const handleZoomOut = () => {
      if (mapRef.current) {
        //console.log('[Map Control] Zooming out');
        const currentZoom = mapRef.current.getZoom();
        mapRef.current.easeTo({
          zoom: currentZoom - 1,
          duration: 3500,
          pitch: parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--map-pitch-min')),
          easing: (t) => t * (2 - t)
        });
      }
    };

    const handleResetView = () => {
      if (mapRef.current && validLocations.length > 0) {
        // Clear info card and selection
        isResetViewRef.current = true; // Mark as reset
        handleCloseInfoCard();
        setActiveMarkerId(null);
        console.log('[MapLibre] Setting Active Marker ID to NULL 2');
        isUserInteractingRef.current = false;
        console.log('[MapLibre] SETTING INFO CARD AND USER INTERACTION REF TO FALSE -- FOKIN HELLLLLL ---------------------- 5555');
        
        let bounds = new maplibregl.LngLatBounds();
        validLocations.forEach(loc => {
          bounds.extend([loc.position.lng, loc.position.lat]);
        });
        
        mapRef.current.fitBounds(bounds, { 
          padding: mapPaddingService.getBoundsPadding(), // Use same padding as auto-flow
          duration: 3000,
          essential: true
        });
      }
    };
    
    window.addEventListener('map-zoom-in', handleZoomIn);
    window.addEventListener('map-zoom-out', handleZoomOut);
    window.addEventListener('map-reset-view', handleResetView);
    
    return () => {
      window.removeEventListener('map-zoom-in', handleZoomIn);
      window.removeEventListener('map-zoom-out', handleZoomOut);
      window.removeEventListener('map-reset-view', handleResetView);
    };
  }, [validLocations]);

  // Track map state changes
  useEffect(() => {
    if (locations.length > 0) {
      mapStateService.updateVisibleLocations(
        locations.map(location => location.id)
      );
    }
  }, [locations]);

  // Add this effect after the other useEffect hooks
  useEffect(() => {
    if (!mapRef.current) return;
    
    const map = mapRef.current;

    const calculatePitch = (zoom: number) => {
      const minZoom = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--map-min-zoom').trim());
      const maxZoom = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--map-max-zoom').trim());
      const minPitch = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--map-pitch-min').trim());
      const maxPitch = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--map-pitch-max').trim());
      
      const clampedZoom = Math.max(minZoom, Math.min(maxZoom, zoom));
      const zoomProgress = (clampedZoom - minZoom) / (maxZoom - minZoom);
      return minPitch + (zoomProgress * (maxPitch - minPitch));
    };
    
    // Override zoom behavior to include pitch
    const originalZoomTo = map.zoomTo.bind(map);
    const originalEaseTo = map.easeTo.bind(map);
    const originalFlyTo = map.flyTo.bind(map);
    
    map.zoomTo = function(zoom: number, options?: any) {
      const pitch = calculatePitch(zoom);
      return originalZoomTo(zoom, { ...options, pitch });
    };
    
    /* map.easeTo = function(options: any) {
      // Add default padding if not specified
      if (!options.padding) {
        options.padding = mapPaddingService.getPadding();
      }
      
      if (options.zoom !== undefined && options.pitch === undefined) {
        options.pitch = calculatePitch(options.zoom);
      }
      return originalEaseTo(options);
    }; */

    map.easeTo = function(options: any) {
      // Add default padding if not specified
      if (!options.padding) {
        options.padding = mapPaddingService.getPadding();
      }
      
      // Check if 'around' looks like pixel coordinates
      if (options.around && Array.isArray(options.around)) {
        const [x, y] = options.around;
        if (Math.abs(y) > 90) {
          // Convert pixel to lng/lat
          options.around = map.unproject([x, y]);
        }
      }
      
      if (options.zoom !== undefined && options.pitch === undefined) {
        options.pitch = calculatePitch(options.zoom);
      }
      return originalEaseTo(options);
    };
    
    map.flyTo = function(options: any) {
      if (options.zoom !== undefined && options.pitch === undefined) {
        options.pitch = calculatePitch(options.zoom);
      }
      return originalFlyTo(options);
    };


    map.on('wheel', (e) => {
      const currentZoom = map.getZoom();
      const targetPitch = calculatePitch(currentZoom);
      
      map.setPitch(targetPitch); // ADD THIS
    });
    
    // Set initial pitch based on current zoom
    const initialPitch = calculatePitch(map.getZoom());
    map.setPitch(initialPitch);
    
    //console.log("Zoom-pitch interpolation initialized");
    
    return () => {
      // Restore original methods
      map.zoomTo = originalZoomTo;
      map.easeTo = originalEaseTo;
      map.flyTo = originalFlyTo;
    };
  }, [mapRef.current]);

return (
  <div className="h-full relative" onClick={handleMapClick}>
    <div ref={mapContainerRef} className="h-full w-full"></div>
    
    {isProcessingLocation && (
      <div className="absolute inset-0 bg-white/30 backdrop-blur-[1px] z-30 pointer-events-none flex items-center justify-center">
        <div className="bg-white/90 rounded-lg px-4 py-2 shadow-lg">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-gray-600 border-t-transparent rounded-full animate-spin" />
            <span className="text-gray-600 font-medium">Discovering locations...</span>
          </div>
        </div>
      </div>
    )}
    
    <MarkerTooltip
      locationName={hoveredLocation?.name ?? null}
      position={tooltipPosition}
      isVisible={!!hoveredLocation && hoveredLocation.id !== selectedMarkerLocation?.id}
      dayNumber={hoveredLocation && 'dayNumber' in hoveredLocation ? 
        (hoveredLocation as any).dayNumber : undefined}
    />
    
    {/* Keep card within map container */}
    <div className="absolute inset-0 pointer-events-none overflow-hidden z-[4000]">
      <MapInfoCard 
        location={showInfoCard ? selectedMarkerLocation : null}
        onClose={handleCloseInfoCard}
        markerPosition={markerPosition}
        images={locationImages}
        //onImagesUpdate={handleImagesUpdate}
        onExpandedChange={handleExpandedChange}
        isClosing={isClosing}
        style={{ 
          position: 'absolute',
          zIndex: 4000,  // Much lower z-index
          pointerEvents: 'auto'  // Re-enable pointer events for the card
        }}
      />
    </div>
  </div>
);
};